import { requestJson } from "./http";

export type Language = "pl" | "en";

const STORAGE_KEY = "avantoai:language";

function detectInitial(): Language {
  if (typeof window === "undefined") return "pl";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "pl" || stored === "en") return stored;
  return window.navigator.language?.toLowerCase().startsWith("en") ? "en" : "pl";
}

let currentLanguage: Language = detectInitial();

function writeLocaleCookie(language: Language) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `locale=${language};path=/;max-age=31536000;SameSite=Lax${secure}`;
}

writeLocaleCookie(currentLanguage);
if (typeof document !== "undefined") document.documentElement.lang = currentLanguage;

export function getLanguage(): Language {
  return currentLanguage;
}

export async function setLanguage(language: Language) {
  if (language === currentLanguage) return;

  try {
    await requestJson<{ ok: boolean; locale: Language }>("/locale", {
      method: "PATCH",
      body: { locale: language },
    });
  } catch (error) {
    console.error("Failed to save language preference", error);
    return;
  }

  persistLanguage(language);
  window.location.reload();
}

export function persistLanguage(language: Language) {
  currentLanguage = language;
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, language);
  writeLocaleCookie(language);
  if (typeof document !== "undefined") document.documentElement.lang = language;
}

export function isLanguage(value: unknown): value is Language {
  return value === "pl" || value === "en";
}

export function adoptServerLanguage(language: unknown): boolean {
  if (!isLanguage(language) || language === currentLanguage) return false;
  persistLanguage(language);
  return true;
}

export function pick<T>(dict: Record<Language, T>): T {
  return dict[currentLanguage];
}

const commonPl = {
  actions: {
    save: "Zapisz",
    saving: "Zapisywanie…",
    cancel: "Anuluj",
    delete: "Usuń",
    edit: "Edytuj",
    close: "Zamknij",
    back: "Wróć",
    confirm: "Potwierdź",
    add: "Dodaj",
    remove: "Usuń",
    filter: "Filtruj",
    clear: "Wyczyść",
    download: "Pobierz",
    retry: "Ponów",
    generateAgain: "Wygeneruj ponownie",
    loading: "Ładowanie…",
    search: "Szukaj",
    open: "Otwórz",
    copy: "Kopiuj",
    copied: "Skopiowano",
    logout: "Wyloguj",
  },
  status: {
    queued: "W kolejce",
    running: "Renderuję…",
    done: "Gotowe",
    failed: "Błąd",
    cancelled: "Anulowano",
  },
  errors: {
    generationInvalidResponse: "Serwer nie zwrócił poprawnych danych generacji.",
    generationStartFailed: "Nie udało się uruchomić generacji. Sprawdź logi web/sidekiq i spróbuj ponownie.",
    parseFailed: "Analiza nie powiodła się. Spróbuj ponownie.",
    parseStartFailed: "Nie udało się uruchomić analizy.",
    retryGenerationFailed: "Nie udało się ponowić generacji tej grafiki.",
    regenerateGenerationFailed: "Nie udało się wygenerować ponownie tej grafiki.",
  },
  language: {
    label: "Język",
    polish: "Polski",
    english: "Angielski",
  },
  auth: {
    email: "E-mail",
    password: "Hasło",
    confirmPassword: "Potwierdź hasło",
    newPassword: "Nowe hasło",
    confirmNewPassword: "Potwierdź nowe hasło",
    backToLogin: "Wróć do logowania",
    signIn: "Zaloguj się",
  },
};

const commonEn: typeof commonPl = {
  actions: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    back: "Back",
    confirm: "Confirm",
    add: "Add",
    remove: "Remove",
    filter: "Filter",
    clear: "Clear",
    download: "Download",
    retry: "Retry",
    generateAgain: "Generate again",
    loading: "Loading…",
    search: "Search",
    open: "Open",
    copy: "Copy",
    copied: "Copied",
    logout: "Log out",
  },
  status: {
    queued: "Queued",
    running: "Rendering…",
    done: "Done",
    failed: "Failed",
    cancelled: "Cancelled",
  },
  errors: {
    generationInvalidResponse: "The server returned an invalid generation response.",
    generationStartFailed: "Could not start generation. Check the web/sidekiq logs and try again.",
    parseFailed: "Product analysis failed. Try again.",
    parseStartFailed: "Could not start product analysis.",
    retryGenerationFailed: "Could not retry this visual.",
    regenerateGenerationFailed: "Could not regenerate this visual.",
  },
  language: {
    label: "Language",
    polish: "Polish",
    english: "English",
  },
  auth: {
    email: "E-mail",
    password: "Password",
    confirmPassword: "Confirm password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    backToLogin: "Back to sign in",
    signIn: "Sign in",
  },
};

const COMMON: Record<Language, typeof commonPl> = { pl: commonPl, en: commonEn };

export function common() {
  return COMMON[currentLanguage];
}
